import {TitleDownloadLink} from '@app/titles/models/title';
import {Trans} from '@common/i18n/trans';
import {SiteSectionHeading} from '@app/titles/site-section-heading';
import {Button} from '@common/ui/buttons/button';
import clsx from 'clsx';
import EmptyData from '../EmptyData';
import {
  downloadFromServer,
  requestDownload,
  RequestDownloadPayload,
  useRequestDownloadLink,
} from '@app/titles/requests/use-title-download-link';
import {Fragment, useState} from 'react';
import {toast} from '@common/ui/toast/toast';
import {useMutation} from '@tanstack/react-query';
import {message} from '@common/i18n/message';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from 'react-bootstrap';
import {qualities} from '@app/admin/titles/title-editor/download-link-editor/select-items';
// import {BASE_VIDEO_SRC} from '@common/http/query-client';
// import {Accordion, AccordionItem} from '@common/ui/accordion/accordion';

interface Props {
  links: TitleDownloadLink[] | undefined;
  isSeries?: boolean;
  onPlayClick?: (epId: number | string) => void;
}

type GroupedData = Record<string, TitleDownloadLink[]>;

export function TitlePageSources({links = [], isSeries, onPlayClick}: Props) {
  const [downloadData, setDownloadData] = useState(links);

  const languagePriority: Record<string, number> = {
    'Persian Dubbed': 1,
    'persian Dubbed': 2,
    'persian dubbed': 3,
    Persian: 4,
    persian: 5,
  };

  const qualityOrder = {
    '480p': 9,
    '720p': 8,
    '1080w': 7,
    '1080s': 6,
    '1080b': 5,
    '1080p': 4,
    '1440p': 3,
    '2160p': 2,
    '4320p': 1,
  };

  const sortedVideos = [...links].sort((a, b) => {
    // Get language priorities (default to 6 if not in priority list)
    const aLangPriority = languagePriority[a.language] || 6;
    const bLangPriority = languagePriority[b.language] || 6;

    // First sort by language priority (lower number = higher priority)
    if (aLangPriority !== bLangPriority) {
      return aLangPriority - bLangPriority;
    }

    // If same language, sort by quality (lower number = higher quality)
    const aQualityPriority =
      qualityOrder[a.quality as keyof typeof qualityOrder] || Infinity;
    const bQualityPriority =
      qualityOrder[b.quality as keyof typeof qualityOrder] || Infinity;

    return aQualityPriority - bQualityPriority;
  });

  const fetchUserIp = async () => {
    try {
      const response = await fetch('https://api.ipify.org');
      const data = await response.text();
      return data;
    } catch (error) {
      console.log('Failed to fetch IP:', error);
    }
  };

  const {mutate} = useMutation({
    mutationFn: (data: {id: string; payload: RequestDownloadPayload}) =>
      requestDownload(data.id, data.payload),
    onSuccess: res => {
      // console.log(res);

      // toast(message('Downloaded!'));
      downloadFromServer(res.url, res.authToken);
    },
  });

  const handleRequest = (id: string) => {
    fetchUserIp().then(userIP => {
      mutate({id: id, payload: {user_ip: userIP as string}});
    });
  };

  type GroupedData = Record<string, Record<string, TitleDownloadLink[]>>;

  // const groupedData: GroupedData = sortedVideos.reduce(
  //   (result: GroupedData, item: TitleDownloadLink) => {
  //     const seasonMatch = item.name.match(/S(\d+)/i);
  //     const episodeMatch = item.name.match(/E(\d+)/i);

  //     const seasonNum = seasonMatch ? parseInt(seasonMatch[1], 10) : 1;
  //     const episodeNum = episodeMatch ? parseInt(episodeMatch[1], 10) : 0;

  //     const season = `S${seasonNum.toString().padStart(3, '0')}`;
  //     const episode = `E${episodeNum.toString().padStart(3, '0')}`;

  //     if (!result[season]) result[season] = {};
  //     if (!result[season][episode]) result[season][episode] = [];

  //     result[season][episode].push(item);
  //     return result;
  //   },
  //   {},
  // );

  const groupedData = sortedVideos.reduce((acc: GroupedData, item) => {
    const seasonMatch = item.name.match(/S(\d+)/i);
    const episodeMatch = item.name.match(/E(\d+)/i);

    const seasonNum = seasonMatch ? parseInt(seasonMatch[1], 10) : 1;
    const episodeNum = episodeMatch ? parseInt(episodeMatch[1], 10) : 0;

    const season = `S${seasonNum.toString().padStart(3, '0')}`;
    const episode = `E${episodeNum.toString().padStart(3, '0')}`;

    if (!acc[season]) acc[season] = {};
    if (!acc[season][episode]) acc[season][episode] = [];

    acc[season][episode].push(item);
    return acc;
  }, {});

  // const sortedSeasons = Object.keys(groupedData).sort();
  // sortedSeasons.forEach(season => {
  //   const episodes = groupedData[season];
  //   const sortedEpisodes = Object.keys(episodes).sort();

  //   sortedEpisodes.forEach(episode => {
  //     episodes[episode].sort((a, b) => {
  //       return (
  //         (qualityOrder[a.quality as keyof typeof qualityOrder] || Infinity) -
  //         (qualityOrder[b.quality as keyof typeof qualityOrder] || Infinity)
  //       );
  //     });
  //   });
  // });

  const sortedSeasons = Object.keys(groupedData).sort(
    (a, b) =>
      parseInt(a.replace('S', ''), 10) - parseInt(b.replace('S', ''), 10),
  );
  sortedSeasons.forEach(season => {
    const episodes = groupedData[season];
    const sortedEpisodes = Object.keys(episodes).sort(
      (a, b) =>
        parseInt(a.replace('E', ''), 10) - parseInt(b.replace('E', ''), 10),
    );
    const sortedEpisodeMap = sortedEpisodes.reduce(
      (acc, episode) => {
        acc[episode] = episodes[episode];
        return acc;
      },
      {} as Record<string, TitleDownloadLink[]>,
    );
    groupedData[season] = sortedEpisodeMap;
  });

  // for (const season in groupedData) {
  //   for (const episode in groupedData[season]) {
  //     groupedData[season][episode].sort((a, b) => {
  //       const qualityOrder: Record<string, number> = {
  //         '480p': 9,
  //         '720p': 8,
  //         '1080p': 7,
  //         '1080w': 6,
  //         '1080s': 5,
  //         '1080b': 4,
  //         '1440p': 3,
  //         '2160p': 2,
  //         '4320p': 1,
  //       };
  //       return (
  //         (qualityOrder[a.quality] || Infinity) -
  //         (qualityOrder[b.quality] || Infinity)
  //       );
  //     });
  //   }
  // }

  return (
    <div className="mt-48">
      <SiteSectionHeading isInTitle>
        <Trans message="Sources" />
      </SiteSectionHeading>
      <div className="source-list-content table-responsive">
        {sortedVideos.length !== 0 ? (
          <table className="custom-table table table-auto">
            <thead>
              <tr>
                {isSeries && (
                  <th>
                    <Trans message="name" />
                  </th>
                )}
                <th>
                  <Trans message="language" />
                </th>
                <th>
                  <Trans message="Quality" />
                </th>
                <th className=" [@media(max-width:767px)]:hidden">
                  <Trans message="Size" />
                </th>{' '}
                <th className=" [@media(max-width:767px)]:hidden">
                  <Trans message="format" />
                </th>
                <th className=" [@media(max-width:767px)]:hidden">
                  <Trans message="encoder" />
                </th>
                <th>
                  <Trans message="Actions" />
                </th>
              </tr>
            </thead>
            <tbody>
              {isSeries ? (
                <tr>
                  <td colSpan={7}>
                    {Object.entries(groupedData)
                      .sort(
                        ([a], [b]) => Number(a.slice(1)) - Number(b.slice(1)),
                      )
                      .map(([season, episodes], index) => {
                        return (
                          <Accordion>
                            <AccordionItem
                              eventKey={index.toString()}
                              // label={season}
                              // labelClassName="text-right"
                              className="text-right"
                            >
                              <AccordionHeader>
                                {season.replace(/([Se])0+(\d+)/g, '$1$2')}
                              </AccordionHeader>
                              <AccordionBody>
                                <Accordion>
                                  {Object.keys(episodes)
                                    .reverse()
                                    .sort(
                                      (a, b) =>
                                        Number(a.slice(1)) - Number(b.slice(1)),
                                    )
                                    .map((episode, i) => (
                                      <AccordionItem
                                        eventKey={i.toString()}
                                        // label={episode}
                                        // labelClassName="text-right"
                                        // bodyClassName=" !p-0"
                                        className="text-right"
                                      >
                                        <AccordionHeader>
                                          {episode.replace(
                                            /([Ee])0+(\d+)/g,
                                            '$1$2',
                                          )}
                                        </AccordionHeader>
                                        <AccordionBody>
                                          {groupedData[season][episode].map(
                                            link => (
                                              <Fragment key={link.id}>
                                                <div
                                                  key={link.id}
                                                  className="flex w-full flex-col gap-y-8 border-y py-10 md:hidden"
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <span>
                                                      {
                                                        link.name?.split('_')[
                                                          link?.name.split('_')
                                                            .length - 2
                                                        ]
                                                      }
                                                    </span>
                                                    <span>
                                                      {
                                                        link.language?.split(
                                                          ',',
                                                        )[0]
                                                      }
                                                    </span>
                                                    <span>
                                                      {
                                                        //@ts-expect-error
                                                        qualities[link.quality]
                                                      }
                                                    </span>
                                                  </div>
                                                  <div className="flex items-center justify-between">
                                                    <Button
                                                      variant="flat"
                                                      className={clsx(
                                                        'transition-colors',
                                                        i === 0
                                                          ? 'bg-[#7F050B] hover:!bg-[#99060D]'
                                                          : i === 1
                                                            ? 'bg-[#99060D] hover:!bg-[#B20710] '
                                                            : i === 2
                                                              ? 'bg-[#B20710] hover:!bg-[#CC0812]'
                                                              : i === 3
                                                                ? 'bg-[#CC0812] hover:!bg-[#E50914]'
                                                                : i === 4
                                                                  ? 'bg-[#E50914] hover:!bg-[#F61622]'
                                                                  : i === 5
                                                                    ? 'bg-[#F61622] hover:!bg-[#F7333D]'
                                                                    : i === 6
                                                                      ? 'bg-[#F7333D] hover:!bg-[#F85159]'
                                                                      : 'bg-[#F85159] hover:!bg-[#f6747a]',
                                                      )}
                                                      onClick={() => {
                                                        if (link.direct === 0) {
                                                          window.open(
                                                            link.name,
                                                          );
                                                        } else {
                                                          handleRequest(
                                                            link.id.toString(),
                                                          );
                                                        }
                                                      }}
                                                    >
                                                      <Trans message="Download" />
                                                    </Button>
                                                    {link.direct ===
                                                    0 ? null : (
                                                      <Button
                                                        variant="flat"
                                                        className={clsx(
                                                          'transition-colors',
                                                          i === 0
                                                            ? 'bg-[#7F050B] hover:!bg-[#99060D]'
                                                            : i === 1
                                                              ? 'bg-[#99060D] hover:!bg-[#B20710] '
                                                              : i === 2
                                                                ? 'bg-[#B20710] hover:!bg-[#CC0812]'
                                                                : i === 3
                                                                  ? 'bg-[#CC0812] hover:!bg-[#E50914]'
                                                                  : i === 4
                                                                    ? 'bg-[#E50914] hover:!bg-[#F61622]'
                                                                    : i === 5
                                                                      ? 'bg-[#F61622] hover:!bg-[#F7333D]'
                                                                      : i === 6
                                                                        ? 'bg-[#F7333D] hover:!bg-[#F85159]'
                                                                        : 'bg-[#F85159] hover:!bg-[#f6747a]',
                                                        )}
                                                        onClick={() => {
                                                          if (!onPlayClick)
                                                            return;
                                                          onPlayClick(
                                                            `${import.meta.env.VITE_BASE_VIDEO_SRC}${link.link}`,
                                                          );
                                                        }}
                                                      >
                                                        <Trans message="Play" />
                                                      </Button>
                                                    )}
                                                    <p className="!m-0 mt-1 block md:hidden">
                                                      {link.size}
                                                    </p>
                                                  </div>
                                                </div>
                                                <tr className="w-full [@media(max-width:767px)]:hidden">
                                                  <td className="w-[15%] [@media(max-width:767px)]:w-[30%]">
                                                    {
                                                      link.name?.split('_')[
                                                        link?.name.split('_')
                                                          .length - 2
                                                      ]
                                                    }
                                                  </td>
                                                  <td className="w-[15%] [@media(max-width:767px)]:w-[10%]">
                                                    {
                                                      link.language?.split(
                                                        ',',
                                                      )[0]
                                                    }
                                                  </td>
                                                  <td className="w-[15%] [@media(max-width:767px)]:w-[30%]">
                                                    {
                                                      //@ts-expect-error
                                                      qualities[link.quality]
                                                    }
                                                  </td>
                                                  <td className=" w-[15%] [@media(max-width:767px)]:hidden">
                                                    {link.size}
                                                  </td>
                                                  <td className=" w-[15%] [@media(max-width:767px)]:hidden">
                                                    {link.format}
                                                  </td>
                                                  <td className=" w-[15%] [@media(max-width:767px)]:hidden">
                                                    {link.direct === 0
                                                      ? 'Indirect Link'
                                                      : link.encoder}
                                                  </td>
                                                  <td className="w-full [@media(max-width:767px)]:w-[30%]">
                                                    <Button
                                                      variant="flat"
                                                      className={clsx(
                                                        'transition-colors',
                                                        i === 0
                                                          ? 'bg-[#7F050B] hover:!bg-[#99060D]'
                                                          : i === 1
                                                            ? 'bg-[#99060D] hover:!bg-[#B20710] '
                                                            : i === 2
                                                              ? 'bg-[#B20710] hover:!bg-[#CC0812]'
                                                              : i === 3
                                                                ? 'bg-[#CC0812] hover:!bg-[#E50914]'
                                                                : i === 4
                                                                  ? 'bg-[#E50914] hover:!bg-[#F61622]'
                                                                  : i === 5
                                                                    ? 'bg-[#F61622] hover:!bg-[#F7333D]'
                                                                    : i === 6
                                                                      ? 'bg-[#F7333D] hover:!bg-[#F85159]'
                                                                      : 'bg-[#F85159] hover:!bg-[#f6747a]',
                                                      )}
                                                      onClick={() => {
                                                        if (link.direct === 0) {
                                                          window.open(
                                                            link.name,
                                                          );
                                                        } else {
                                                          handleRequest(
                                                            link.id.toString(),
                                                          );
                                                        }
                                                      }}
                                                    >
                                                      <Trans message="Download" />
                                                    </Button>
                                                    {link.direct === 0 ||
                                                    link.link.includes(
                                                      'mkv',
                                                    ) ? null : (
                                                      <Button
                                                        variant="flat"
                                                        className={clsx(
                                                          'transition-colors',
                                                          i === 0
                                                            ? 'bg-[#7F050B] hover:!bg-[#99060D]'
                                                            : i === 1
                                                              ? 'bg-[#99060D] hover:!bg-[#B20710] '
                                                              : i === 2
                                                                ? 'bg-[#B20710] hover:!bg-[#CC0812]'
                                                                : i === 3
                                                                  ? 'bg-[#CC0812] hover:!bg-[#E50914]'
                                                                  : i === 4
                                                                    ? 'bg-[#E50914] hover:!bg-[#F61622]'
                                                                    : i === 5
                                                                      ? 'bg-[#F61622] hover:!bg-[#F7333D]'
                                                                      : i === 6
                                                                        ? 'bg-[#F7333D] hover:!bg-[#F85159]'
                                                                        : 'bg-[#F85159] hover:!bg-[#f6747a]',
                                                        )}
                                                        onClick={() => {
                                                          if (!onPlayClick)
                                                            return;
                                                          onPlayClick(
                                                            `${import.meta.env.VITE_BASE_VIDEO_SRC}${link.link}`,
                                                          );
                                                        }}
                                                      >
                                                        <Trans message="Play" />
                                                      </Button>
                                                    )}
                                                  </td>
                                                </tr>
                                              </Fragment>
                                            ),
                                          )}
                                        </AccordionBody>
                                      </AccordionItem>
                                    ))}
                                </Accordion>
                              </AccordionBody>
                            </AccordionItem>
                          </Accordion>
                        );
                      })}{' '}
                  </td>
                </tr>
              ) : null}
              {!isSeries
                ? sortedVideos.map((link, i) => (
                    <tr key={link.id}>
                      {isSeries && (
                        <td>
                          {
                            link.name?.split('_')[
                              link?.name.split('_').length - 2
                            ]
                          }
                        </td>
                      )}
                      <td>
                        <Trans message={link.language?.split(',')[0]} />
                      </td>
                      <td>
                        {
                          //@ts-expect-error
                          qualities[link.quality]
                        }
                      </td>
                      <td className=" [@media(max-width:767px)]:hidden">
                        {link.size}
                      </td>
                      <td className=" [@media(max-width:767px)]:hidden">
                        {link.format}
                      </td>
                      <td className=" [@media(max-width:767px)]:hidden">
                        {link.direct === 0 ? 'Indirect Link' : link.encoder}
                      </td>
                      <td className="flex flex-col items-center md:flex-row">
                        <Button
                          variant="flat"
                          className={clsx(
                            'transition-colors',
                            i === 0
                              ? 'bg-[#7F050B] hover:!bg-[#99060D]'
                              : i === 1
                                ? 'bg-[#99060D] hover:!bg-[#B20710] '
                                : i === 2
                                  ? 'bg-[#B20710] hover:!bg-[#CC0812]'
                                  : i === 3
                                    ? 'bg-[#CC0812] hover:!bg-[#E50914]'
                                    : i === 4
                                      ? 'bg-[#E50914] hover:!bg-[#F61622]'
                                      : i === 5
                                        ? 'bg-[#F61622] hover:!bg-[#F7333D]'
                                        : i === 6
                                          ? 'bg-[#F7333D] hover:!bg-[#F85159]'
                                          : 'bg-[#F85159] hover:!bg-[#f6747a]',
                          )}
                          onClick={() => {
                            if (link.direct === 0) {
                              window.open(link.name);
                            } else {
                              handleRequest(link.id.toString());
                            }
                          }}
                        >
                          <Trans message="Download" />
                        </Button>
                        {link.direct === 0 ||
                        link.link.includes('mkv') ? null : (
                          <Button
                            variant="flat"
                            className={clsx(
                              'transition-colors',
                              i === 0
                                ? 'bg-[#7F050B] hover:!bg-[#99060D]'
                                : i === 1
                                  ? 'bg-[#99060D] hover:!bg-[#B20710] '
                                  : i === 2
                                    ? 'bg-[#B20710] hover:!bg-[#CC0812]'
                                    : i === 3
                                      ? 'bg-[#CC0812] hover:!bg-[#E50914]'
                                      : i === 4
                                        ? 'bg-[#E50914] hover:!bg-[#F61622]'
                                        : i === 5
                                          ? 'bg-[#F61622] hover:!bg-[#F7333D]'
                                          : i === 6
                                            ? 'bg-[#F7333D] hover:!bg-[#F85159]'
                                            : 'bg-[#F85159] hover:!bg-[#f6747a]',
                            )}
                            onClick={() => {
                              if (!onPlayClick) return;
                              onPlayClick(
                                `${import.meta.env.VITE_BASE_VIDEO_SRC}${link.link}`,
                              );
                            }}
                          >
                            <Trans message="Play" />
                          </Button>
                        )}
                        <p className="!m-0 mt-1 block md:hidden">{link.size}</p>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        ) : (
          <EmptyData />
        )}
      </div>
    </div>
  );
}
